import {
  Agent,
  AgentFUB,
  CustomAgentFUB,
  MappedAgent,
  StrapiAgent,
} from '../../apolloClient/types/Agents';
import { StrapiMediaInfo } from '../../apolloClient/types/StrapiMediaInfo';
import {
  StrapiResponse,
  StrapiResponseData,
} from '../../apolloClient/types/StrapiResponse';
import { getMediumImageUrlFromImage } from '../../lib/getImages';
import { flattenStrapiDataItem } from '../../lib/flattenStrapiBulkDataItems';

export const defaultAgentImage =
  'https://media.architecturaldigest.com/photos/5e679e7d9f1ae10008cf0e1e/16:9/w_2560%2Cc_limit/GettyImages-1203653538.jpg';

// `photo` may arrive wrapped (regular Strapi GraphQL: { data: { attributes }})
// or flat (custom resolver `getCustomUserAgents`: { id, url, formats, ... }).
type AgentPhoto =
  | StrapiResponse<StrapiResponseData<StrapiMediaInfo>>
  | StrapiMediaInfo
  | null
  | undefined;

function toStrapiMediaInfo(photo: AgentPhoto): StrapiMediaInfo | undefined {
  if (!photo) return undefined;
  if ('data' in photo) {
    return photo.data ? flattenStrapiDataItem(photo.data) : undefined;
  }
  if ('url' in photo) return photo;
  return undefined;
}

// Strapi media first (fast, CDN-served), FUB picture fallback, default last.
function resolveAgentImage(
  photo: AgentPhoto,
  fubPictureUrl: string | undefined
): string {
  const media = toStrapiMediaInfo(photo);
  if (media) {
    const url = getMediumImageUrlFromImage(media);
    if (url) return url;
  }
  return fubPictureUrl || defaultAgentImage;
}

// Anchor id of an agent's block on /about (linked from expert cards).
export function getAgentAnchorId(
  firstName?: string,
  lastName?: string
): string {
  return [firstName, lastName]
    .filter(Boolean)
    .join('-')
    .toLowerCase()
    .replace(/\s+/g, '-');
}

function buildMappedAgent(params: {
  name: string;
  email: string;
  fubEmail?: string;
  phone?: string;
  job?: string;
  company?: string;
  image: string;
}): MappedAgent {
  // Everything after the first word is the last name, so multi-part surnames
  // (e.g. "Tatiana Rodriguez Fenn") are kept in full.
  const [firstName, ...rest] = params.name?.trim().split(/\s+/) || [];
  const lastName = rest.join(' ');
  return {
    firstName,
    lastName,
    email: params.email,
    fubEmail: params.fubEmail,
    job: params.job,
    phone: params.phone,
    company: params.company,
    image: params.image,
  };
}

export function mapAgent(
  agent: Agent | StrapiAgent | AgentFUB | null
): MappedAgent | null {
  if (!agent) return null;

  // Parent Agent has an `agentId` FUB relation — prefer direct Strapi fields,
  // fall back to FUB (agentId) when a Strapi field is empty.
  if ('agentId' in agent) {
    const fub = agent.agentId;
    return buildMappedAgent({
      name: agent.name || fub?.name || '',
      email: agent.email || fub?.email || '',
      fubEmail: fub?.email,
      phone: agent.phone || fub?.phone,
      job: agent.job || fub?.job,
      company: agent.company || fub?.company,
      image: resolveAgentImage(agent.photo, fub?.picture?.original),
    });
  }

  // Single-source agent: StrapiAgent (may carry `photo`) or AgentFUB (no photo).
  // Both expose `picture.original` from FUB as a fallback.
  const photo = (agent as StrapiAgent).photo;
  return buildMappedAgent({
    name: agent.name || '',
    email: agent.email || '',
    fubEmail: agent.email,
    phone: agent.phone,
    job: agent.job,
    company: agent.company,
    image: resolveAgentImage(photo, agent.picture?.original),
  });
}

export function mapCustomAgent(
  agent: CustomAgentFUB | undefined
): MappedAgent | null {
  if (!agent) return null;
  const { agentData } = agent;
  return buildMappedAgent({
    name: agentData?.name || agent.name || '',
    email: agentData?.email || agent.email || '',
    fubEmail: agent.email,
    phone: agentData?.phone || agent.phone,
    job: agent.job || agentData?.job,
    company: agent.company || agentData?.company,
    image: resolveAgentImage(agentData?.photo, agent.picture?.original),
  });
}
